/**
 * Cosmetics v3 ingredient story carousel: vanilla custom element, no dependencies.
 *
 * The track scrolls natively, so swipe, trackpad and touch drag come from the browser. The arrows
 * move the track by one card with an eased animation. Looping wraps from the last card to the first.
 * Reduced-motion users get instant moves. Listeners and frames are removed on disconnect.
 */
const EASE = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

class Cv3IngredientStory extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.track = this.querySelector('[data-cv3-ing-track]');
    this.cards = this.track ? [...this.track.querySelectorAll('[data-cv3-ing-card]')] : [];
    this.prev = this.querySelector('[data-cv3-ing-prev]');
    this.next = this.querySelector('[data-cv3-ing-next]');
    this.status = this.querySelector('[data-cv3-ing-status]');

    if (!this.track || this.cards.length < 2) return;

    this.loop = this.dataset.loop === 'true';
    this.speed = Number(this.dataset.speed) || 250;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isRtl = getComputedStyle(this).direction === 'rtl';
    this.sign = this.isRtl ? -1 : 1;
    this.frame = null;

    this.prev?.addEventListener('click', () => this.step(-1), { signal });
    this.next?.addEventListener('click', () => this.step(1), { signal });
    this.track.addEventListener('scroll', () => this.sync(), { passive: true, signal });

    // Theme Editor: selecting a card brings it into view.
    document.addEventListener('shopify:block:select', (event) => {
      const card = event.target?.closest?.('[data-cv3-ing-card]');
      if (!card || !this.contains(card)) return;
      this.animateTo(this.clamp(card.offsetLeft - this.track.offsetLeft), 0);
    }, { signal });

    this.resizeObserver = new ResizeObserver(() => this.sync());
    this.resizeObserver.observe(this.track);
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  // Logical position: distance from the start, the same in LTR and RTL.
  get position() {
    return Math.abs(this.track.scrollLeft);
  }

  get maxPosition() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  // One step is one card plus the gap to the next card.
  get stepSize() {
    return this.cards[1].offsetLeft - this.cards[0].offsetLeft;
  }

  clamp(value) {
    return Math.min(Math.max(value, 0), this.maxPosition);
  }

  step(direction) {
    const max = this.maxPosition;
    const atEnd = direction > 0 ? this.position >= max - 1 : this.position <= 1;
    if (atEnd) {
      // At an end: wrap when looping is on, otherwise the arrows stop here.
      if (this.loop) this.animateTo(direction > 0 ? 0 : max);
      return;
    }
    // Slide one card, but land on the real end instead of overshooting it.
    this.animateTo(this.clamp(this.position + direction * this.stepSize));
  }

  animateTo(target, duration = this.speed) {
    if (this.frame) cancelAnimationFrame(this.frame);
    const start = this.position;
    const delta = target - start;
    const ms = this.reducedMotion ? 0 : duration;
    if (!delta || ms === 0) {
      this.setPosition(target);
      this.frame = null;
      return;
    }
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / ms);
      this.setPosition(start + delta * EASE(t));
      this.frame = t < 1 ? requestAnimationFrame(tick) : null;
    };
    this.frame = requestAnimationFrame(tick);
  }

  setPosition(value) {
    this.track.scrollLeft = this.sign * value;
  }

  // Update the status text and the disabled state of the arrows.
  sync() {
    if (!this.track) return;
    const max = this.maxPosition;
    // Nothing to scroll: hide the arrows instead of showing controls that do nothing.
    const nav = this.querySelector('.cv3-ing__nav');
    if (nav) nav.hidden = max <= 1;
    const index = this.stepSize > 0 ? Math.round(this.position / this.stepSize) : 0;
    if (this.status) {
      this.status.textContent = (this.dataset.statusTemplate || '')
        .replace('#CURRENT#', String(Math.min(index + 1, this.cards.length)))
        .replace('#TOTAL#', String(this.cards.length));
    }
    if (!this.loop) {
      if (this.prev) this.prev.disabled = this.position <= 1;
      if (this.next) this.next.disabled = this.position >= max - 1;
    }
  }
}

if (!customElements.get('cv3-ingredient-story')) {
  customElements.define('cv3-ingredient-story', Cv3IngredientStory);
}
